// ================================================================
//  JobInstancesDialogController — "Execution History" dialog.
//  Rebuilt to match MetricsExplorerDialogController's UX/capabilities
//  1:1 (same rzslider-based time-range/interval pickers driven by the
//  job's REAL data bounds instead of a fixed "N days back" window, same
//  Live Monitoring toggle, same metric-toggle chips, same resolution/
//  pagination pattern, same Chart.js line chart) — adapted for jobs:
//   - No environment nav — a job execution isn't scoped to an environment.
//   - Interval unit is MINUTES (1-1440), not seconds — the backend's
//     aggregation endpoint only supports minute-granularity buckets
//     (JobInstanceController#getAggregatedInstances), and no job in this
//     system fires more than once every ~30s anyway.
//   - 3 toggleable metrics (Successful/Failed/Avg Execution Time) instead
//     of the interceptor's 8 — success/failure share a count axis, avg
//     time gets its own ms axis.
//   - Live Monitoring has no natural "sync interval" to key off of (a
//     job's own cron schedule isn't a fixed period in the general case),
//     so it polls at a fixed, config-driven cadence instead
//     (frontend.jobs.live-poll-interval-secs, default 15s).
//
//  Locals injected:
//    - jobDefinition : the job row (jobId, jobName, ...)
// ================================================================

angular.module('bizmetryApp').controller('JobInstancesDialogController', function (
  $scope, $mdDialog, $timeout, jobDefinition, jobService, ConfigurationService, LiveChartViewport
) {
    // Live charts show twenty buckets at the live aggregation interval.
    const LIVE_POINT_COUNT = 20;
  $scope.job = jobDefinition;
  $scope.loading = true;
  $scope.timeRangeLoading = true;
  $scope.aggregatedData = [];
  $scope.totalExecutions = 0;
  $scope.noDataMessage = '';
  $scope.isLoadingChart = false;
  $scope.isChangingInterval = false;
  $scope.activeTimePreset = null;

  $scope.page = 0;
  $scope.totalPages = 0;
  $scope.pageSizeOptions = [15, 30, 45, 60];

  // ========================================
  // LIVE MONITORING
  // ========================================

  $scope.isLiveMode = false;
  var liveMonitoringInterval = null;
  var liveWindowMs = null;

  var livePollSecs = ConfigurationService.getFromCache('frontend.jobs.live-poll-interval-secs') || 15;

  // ========================================
  // METRICS SELECTION
  // ========================================

  var METRIC_DEFS = {
    successCount:     { label: 'Successful',        icon: 'check_circle',   color: '#4caf50', textColor: '#2e7d32', axis: 'y-count' },
    failureCount:      { label: 'Failed',             icon: 'error_outline',   color: '#f44336', textColor: '#c62828', axis: 'y-count' },
    avgExecutionTime: { label: 'Avg Execution Time', icon: 'timer',            color: '#2196f3', textColor: '#1565c0', axis: 'y-time' }
  };
  $scope.metricDefs = METRIC_DEFS;
  $scope.metricKeys = Object.keys(METRIC_DEFS);

  $scope.metrics = {
    successCount: true,
    failureCount: true,
    avgExecutionTime: true
  };

  $scope.toggleMetric = function () {
    $scope.renderChart();
  };

  $scope.hasAnyMetricSelected = function () {
    return $scope.metricKeys.some(function (k) { return $scope.metrics[k]; });
  };

  $scope.allMetricsSelected = function () {
    return $scope.metricKeys.every(function (k) { return $scope.metrics[k]; });
  };

  $scope.selectAllMetrics = function () {
    $scope.metricKeys.forEach(function (k) { $scope.metrics[k] = true; });
    $scope.renderChart();
  };

  $scope.clearAllMetrics = function () {
    $scope.metricKeys.forEach(function (k) { $scope.metrics[k] = false; });
    $scope.renderChart();
  };

  // ========================================
  // CONFIG
  // ========================================

  var minIntervalMins = ConfigurationService.getFromCache('frontend.jobs.min-job-aggregation-interval-mins') || 1;
  var maxIntervalMins = ConfigurationService.getFromCache('frontend.jobs.max-job-aggregation-interval-mins') || 1440;
  var defaultIntervalMins = ConfigurationService.getFromCache('frontend.jobs.default-job-aggregation-interval-mins') || 15;
  var defaultPageSize = ConfigurationService.getFromCache('frontend.jobs.default-job-page-size') || 30;

  $scope.pageSize = defaultPageSize;

  var actualMinTimestamp = null;
  var actualMaxTimestamp = null;

  var intervalStep = 1;

  // ========================================
  // TIME RANGE SLIDER — floor/ceil get set to the job's REAL data bounds
  // once initializeTimeRange resolves (see below); these placeholders just
  // give rzslider something non-empty to render before that first fetch.
  // ========================================

  var placeholderNow = Date.now();

  $scope.timeRangeSlider = {
    minValue: placeholderNow - 86400000,
    maxValue: placeholderNow,
    options: {
      floor: placeholderNow - 86400000,
      ceil: placeholderNow,
      step: 60000,
      minRange: 60000,
      pushRange: true,
      draggableRange: false,
      showSelectionBar: true,
      noSwitching: true,
      enforceRange: true,
      enforceStep: false,
      disabled: true,

      translate: function (value) {
        if (!value || value < 0 || !isFinite(value)) { return 'Invalid date'; }
        var date = new Date(value);
        if (isNaN(date.getTime())) { return 'Invalid date'; }
        var sliderMin = $scope.timeRangeSlider.minValue || 0;
        var sliderMax = $scope.timeRangeSlider.maxValue || Date.now();
        var rangeDays = (sliderMax - sliderMin) / 86400000;
        if (rangeDays < 2) {
          return date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        }
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      },

      onEnd: function () {
        if (!$scope.timeRangeSlider.minValue || !$scope.timeRangeSlider.maxValue) { return; }
        if ($scope.timeRangeSlider.minValue >= $scope.timeRangeSlider.maxValue) { return; }
        triggerTimeRangeChangeFromSlider();
      }
    }
  };

  // ========================================
  // AGGREGATION INTERVAL SLIDER (minutes)
  // ========================================

  $scope.intervalSlider = {
    value: defaultIntervalMins,
    options: {
      floor: minIntervalMins,
      ceil: maxIntervalMins,
      step: intervalStep,
      enforceStep: true,
      showSelectionBar: true,

      translate: function (value) {
        if (value < 60) { return value + (value === 1 ? ' min' : ' mins'); }
        if (value < 1440) { var hours = Math.round(value / 60); return hours + (hours === 1 ? ' hour' : ' hours'); }
        var days = Math.round(value / 1440);
        return days + (days === 1 ? ' day' : ' days');
      },

      disabled: true,
      onEnd: function () { onIntervalChange(); }
    }
  };

  $scope.onLiveModeChange = function () {
    $timeout(function () { $scope.toggleLiveMode(); }, 0);
  };

  var savedLiveState = null;

  $scope.toggleLiveMode = function () {
    if ($scope.isLiveMode) {
      savedLiveState = {
        intervalValue: $scope.intervalSlider.value,
        pageSize: $scope.pageSize,
        timeRange: { min: $scope.timeRangeSlider.minValue, max: $scope.timeRangeSlider.maxValue },
        timePreset: $scope.activeTimePreset
      };

      $scope.timeRangeSlider.options.disabled = true;
      $scope.intervalSlider.options.disabled = true;
      $scope.pageSize = LIVE_POINT_COUNT;
      $scope.activeTimePreset = null;
      $scope.page = 0;

      // Twenty histogram buckets at the configured live aggregation interval.
      liveWindowMs = LIVE_POINT_COUNT * minIntervalMins * 60 * 1000;

      if (actualMaxTimestamp) {
        var to = actualMaxTimestamp;
        var from = Math.max(actualMinTimestamp || 0, to - liveWindowMs);
        $scope.timeRangeSlider.minValue = from;
        $scope.timeRangeSlider.maxValue = to;
      }

      $timeout(function () {
        $scope.intervalSlider.value = minIntervalMins;
        $scope.$broadcast('rzSliderForceRender');
        $scope.loadInstances();
        startLiveMonitoring();
      }, 0);
    } else {
      stopLiveMonitoring();
      liveWindowMs = null;
      $scope.timeRangeSlider.options.disabled = false;
      $scope.intervalSlider.options.disabled = false;

      if (savedLiveState) {
        $scope.intervalSlider.value = savedLiveState.intervalValue;
        $scope.pageSize = savedLiveState.pageSize;
        $scope.timeRangeSlider.minValue = savedLiveState.timeRange.min;
        $scope.timeRangeSlider.maxValue = savedLiveState.timeRange.max;
        $scope.activeTimePreset = savedLiveState.timePreset;
        savedLiveState = null;
      }

      $timeout(function () {
        $scope.$broadcast('rzSliderForceRender');
        $scope.page = 0;
        $scope.loadInstances();
      }, 0);
    }
  };

  function startLiveMonitoring() {
    if (liveMonitoringInterval) { clearInterval(liveMonitoringInterval); }

    liveMonitoringInterval = setInterval(function () {
      if (!$scope.isLiveMode) { stopLiveMonitoring(); return; }
      try {
        $scope.$apply(function () { $scope.loadInstances(); });
      } catch (e) {
        $scope.loadInstances();
      }
    }, livePollSecs * 1000);
  }

  function stopLiveMonitoring() {
        if (chartInstance) LiveChartViewport.cancel(chartInstance);
    if (liveMonitoringInterval) {
      clearInterval(liveMonitoringInterval);
      liveMonitoringInterval = null;
    }
  }

  // True only when this job has NEVER executed at all (initializeTimeRange
  // found no min/max) — as opposed to just having no runs in the currently-
  // selected window, where the controls stay usable so the user can go
  // looking for a window that does have data.
  $scope.noDataAtAll = true;

  // ========================================
  // TIME RANGE INITIALIZATION
  // ========================================

  function initializeTimeRange() {
    $scope.timeRangeLoading = true;

    jobService.getJobInstancesTimeRange(jobDefinition.jobId, !!($scope.isLiveMode && chartInstance))
      .then(function (response) {
        var data = response.status === 204 ? null : response.data;

        if (!data || !data.minTimestamp || !data.maxTimestamp || data.minTimestamp >= data.maxTimestamp) {
          $scope.noDataMessage = 'This job has never executed yet';
          $scope.aggregatedData = [];
          $scope.noDataAtAll = true;
          $scope.timeRangeSlider.options.disabled = true;
          $scope.intervalSlider.options.disabled = true;
          $scope.timeRangeLoading = false;
          $scope.loading = false;
          return;
        }

        $scope.noDataAtAll = false;
        $scope.intervalSlider.options.disabled = false;

        actualMinTimestamp = data.minTimestamp;
        actualMaxTimestamp = data.maxTimestamp;

        $scope.timeRangeSlider.options.floor = actualMinTimestamp;
        $scope.timeRangeSlider.options.ceil = actualMaxTimestamp;

        var rangeMs = actualMaxTimestamp - actualMinTimestamp;
        var step = Math.max(60000, Math.round(rangeMs * 0.01));
        $scope.timeRangeSlider.options.step = step;
        $scope.timeRangeSlider.options.minRange = step;

        var oneHourMs = 60 * 60 * 1000;
        if (rangeMs > oneHourMs) {
          $scope.timeRangeSlider.minValue = actualMaxTimestamp - oneHourMs;
          $scope.timeRangeSlider.maxValue = actualMaxTimestamp;
          $scope.activeTimePreset = 'hour';
        } else {
          $scope.timeRangeSlider.minValue = actualMinTimestamp;
          $scope.timeRangeSlider.maxValue = actualMaxTimestamp;
        }

        $scope.timeRangeSlider.options.disabled = false;

        $timeout(function () {
          $scope.$broadcast('rzSliderForceRender');
          $scope.timeRangeLoading = false;
          $scope.page = 'LAST';
          $scope.loadInstances();
        }, 50);
      })
      .catch(function (error) {
        console.error('[JobInstances] Error fetching time range:', error);
        $scope.timeRangeLoading = false;
        $scope.loading = false;
        $scope.noDataMessage = 'Error loading time range';
      });
  }

  // ========================================
  // AVAILABLE INTERVALS — narrows the interval preset choices to what
  // makes sense for the current time-range span, same buckets as
  // MetricsExplorerDialogController#getAvailableIntervals, in minutes.
  // ========================================

  function getAvailableIntervals() {
    var rangeMs = $scope.timeRangeSlider.maxValue - $scope.timeRangeSlider.minValue;
    var rangeMinutes = rangeMs / 60000;
    var rangeHours = rangeMinutes / 60;
    var rangeDays = rangeHours / 24;

    var candidates;
    if (rangeHours <= 1) { candidates = [1, 5, 15]; }
    else if (rangeHours <= 24) { candidates = [5, 15, 30, 60]; }
    else if (rangeDays < 6.0) { candidates = [30, 60, 1440]; }
    else { candidates = [15, 30, 60, 1440]; }

    var available = candidates.filter(function (m) { return m <= maxIntervalMins; });
    if (available.length === 0) { available.push(candidates[0]); }
    return available;
  }

  $scope.isIntervalAvailable = function (minutes) {
    return getAvailableIntervals().indexOf(minutes) > -1;
  };

  function adjustIntervalIfNeeded() {
    var available = getAvailableIntervals();
    if (available.indexOf($scope.intervalSlider.value) === -1) {
      var closest = available[0];
      var minDiff = Math.abs($scope.intervalSlider.value - closest);
      available.forEach(function (iv) {
        var diff = Math.abs($scope.intervalSlider.value - iv);
        if (diff < minDiff) { minDiff = diff; closest = iv; }
      });
      $scope.intervalSlider.value = closest;
      return true;
    }
    return false;
  }

  var timeRangeChangeTimeout = null;
  var intervalChangeTimeout = null;

  function triggerTimeRangeChangeFromPreset() {
    if (timeRangeChangeTimeout) { $timeout.cancel(timeRangeChangeTimeout); }
    $scope.isChangingInterval = true;
    timeRangeChangeTimeout = $timeout(function () {
      if ($scope.page !== 'LAST') { $scope.page = 0; }
      $scope.loadInstances();
    }, 500);
  }

  function triggerTimeRangeChangeFromSlider() {
    if (timeRangeChangeTimeout) { $timeout.cancel(timeRangeChangeTimeout); }
    $scope.isChangingInterval = true;
    $scope.activeTimePreset = null;

    var adjusted = adjustIntervalIfNeeded();

    timeRangeChangeTimeout = $timeout(function () {
      $scope.page = 0;
      if (adjusted) {
        $scope.$broadcast('rzSliderForceRender');
        $timeout(function () { $scope.loadInstances(); }, 100);
      } else {
        $scope.loadInstances();
      }
    }, 500);
  }

  function onIntervalChange() {
    if (intervalChangeTimeout) { $timeout.cancel(intervalChangeTimeout); }
    $scope.isChangingInterval = true;
    intervalChangeTimeout = $timeout(function () {
      $scope.page = 0;
      $scope.loadInstances();
    }, 300);
  }

  // ========================================
  // PRESETS
  // ========================================

  $scope.hasCustomTimeRange = function () {
    if (!actualMinTimestamp || !actualMaxTimestamp) { return false; }
    return $scope.timeRangeSlider.minValue !== actualMinTimestamp ||
      $scope.timeRangeSlider.maxValue !== actualMaxTimestamp;
  };

  $scope.resetTimeRange = function () {
    if (!actualMinTimestamp || !actualMaxTimestamp) { return; }
    $scope.timeRangeSlider.minValue = actualMinTimestamp;
    $scope.timeRangeSlider.maxValue = actualMaxTimestamp;
    $scope.activeTimePreset = null;
    $timeout(function () { $scope.$broadcast('rzSliderForceRender'); }, 50);
    triggerTimeRangeChangeFromSlider();
  };

  function computePresetRange(presetKey) {
    var to = actualMaxTimestamp;
    var from;
    if (presetKey === 'min') {
      from = Math.max(actualMinTimestamp, to - (10 * 60 * 1000));
    } else if (presetKey === 'hour') {
      from = Math.max(actualMinTimestamp, to - (60 * 60 * 1000));
    } else {
      var days = Number(presetKey);
      if (days === 1) {
        from = Math.max(actualMinTimestamp, to - (24 * 60 * 60 * 1000));
      } else {
        var toDate = new Date(to);
        toDate.setHours(23, 59, 59, 999);
        var fromDate = new Date(toDate);
        fromDate.setDate(fromDate.getDate() - days);
        fromDate.setHours(0, 0, 0, 0);
        from = Math.max(actualMinTimestamp, fromDate.getTime());
        to = Math.min(actualMaxTimestamp, toDate.getTime());
      }
    }
    return { from: from, to: to };
  }

  function applyPreset(fromTimestamp, toTimestamp, presetKey) {
    $scope.timeRangeSlider.minValue = fromTimestamp;
    $scope.timeRangeSlider.maxValue = toTimestamp;
    $scope.activeTimePreset = presetKey;

    $timeout(function () {
      $scope.$broadcast('rzSliderForceRender');
      $timeout(function () {
        var adjusted = adjustIntervalIfNeeded();
        $scope.page = 'LAST';
        if (adjusted) {
          $scope.$broadcast('rzSliderForceRender');
          $timeout(function () { triggerTimeRangeChangeFromPreset(); }, 100);
        } else {
          triggerTimeRangeChangeFromPreset();
        }
      }, 100);
    }, 50);
  }

  $scope.setLast10MinPreset = function () {
    if (!actualMaxTimestamp) { return; }
    var range = computePresetRange('min');
    applyPreset(range.from, range.to, 'min');
  };

  $scope.setLastHourPreset = function () {
    if (!actualMaxTimestamp) { return; }
    var range = computePresetRange('hour');
    applyPreset(range.from, range.to, 'hour');
  };

  $scope.setTimeRangePreset = function (days) {
    if (!actualMinTimestamp || !actualMaxTimestamp) { return; }
    var range = computePresetRange(days);
    applyPreset(range.from, range.to, days);
  };

  $scope.setIntervalPreset = function (minutes) {
    if (!$scope.isIntervalAvailable(minutes)) { return; }

    var effective = Math.max(minIntervalMins, Math.min(minutes, maxIntervalMins));
    if ($scope.intervalSlider.value === effective) { return; }

    $scope.intervalSlider.value = effective;
    $scope.isChangingInterval = true;

    $timeout(function () {
      $scope.$broadcast('rzSliderForceRender');
      $timeout(function () { $scope.isChangingInterval = false; }, 0);
    }, 0);

    if (intervalChangeTimeout) { $timeout.cancel(intervalChangeTimeout); }
    $scope.page = 0;
    $scope.loadInstances();
  };

  // ========================================
  // DATA LOADING
  // ========================================

  function timestampToISO(timestamp) {
    return new Date(timestamp).toISOString();
  }

  $scope.loadInstances = function () {
    $scope.isLoadingChart = !($scope.isLiveMode && chartInstance);
    $scope.noDataMessage = '';

    // Live Monitoring re-anchors the sliding window to the SERVER's latest
    // ingested timestamp on every tick, same principle as
    // MetricsExplorerDialogController#loadMetrics.
    if ($scope.isLiveMode) {
      jobService.getJobInstancesTimeRange(jobDefinition.jobId, !!($scope.isLiveMode && chartInstance))
        .then(function (response) {
          var data = response.status === 204 ? null : response.data;
          if (data && data.maxTimestamp) {
            actualMaxTimestamp = data.maxTimestamp;
            var windowMs = liveWindowMs || (LIVE_POINT_COUNT * minIntervalMins * 60 * 1000);
            var to = actualMaxTimestamp;
            var from = Math.max(actualMinTimestamp || 0, to - windowMs);
            $scope.timeRangeSlider.minValue = from;
            $scope.timeRangeSlider.maxValue = to;
          }
          loadInstancesInternal();
        })
        .catch(function () { loadInstancesInternal(); });
      return;
    }

    loadInstancesInternal();
  };

  function loadInstancesInternal() {
    var startTs = $scope.timeRangeSlider.minValue;
    var endTs = $scope.timeRangeSlider.maxValue;
    var intervalMinutes = $scope.intervalSlider.value;
    var wantsLastPage = $scope.page === 'LAST';
    var requestPage = wantsLastPage ? 0 : $scope.page;

    if (!startTs || !endTs || startTs >= endTs) {
      $scope.noDataMessage = 'Invalid time range';
      $scope.isLoadingChart = false;
      $scope.isChangingInterval = false;
      $scope.loading = false;
      return;
    }

    jobService.getJobInstancesAggregated(
      jobDefinition.jobId, intervalMinutes, timestampToISO(startTs), timestampToISO(endTs), requestPage, $scope.pageSize, !!($scope.isLiveMode && chartInstance)
    ).then(function (response) {
      if (response.status === 204 || !response.data) {
        $scope.noDataMessage = 'No executions found in the selected time range';
        $scope.aggregatedData = [];
        $scope.totalPages = 0;
        $scope.totalExecutions = 0;
        $scope.page = 0;
        return null;
      }

      $scope.totalPages = response.data.totalPages || 0;
      $scope.totalExecutions = response.data.totalElements || 0;

      if (wantsLastPage && $scope.totalPages > 0) {
        var lastPage = $scope.totalPages - 1;
        $scope.page = lastPage;
        return jobService.getJobInstancesAggregated(
          jobDefinition.jobId, intervalMinutes, timestampToISO(startTs), timestampToISO(endTs), lastPage, $scope.pageSize, !!($scope.isLiveMode && chartInstance)
        ).then(function (lastResp) {
          if (lastResp.status === 204 || !lastResp.data) {
            $scope.noDataMessage = 'No executions found';
            $scope.aggregatedData = [];
            return;
          }
          $scope.aggregatedData = lastResp.data.content || [];
          if ($scope.aggregatedData.length) { $scope.renderChart(); }
          else { $scope.noDataMessage = 'No executions found'; }
        });
      }

      $scope.aggregatedData = response.data.content || [];
      if (wantsLastPage) { $scope.page = 0; }
      if ($scope.aggregatedData.length) { $scope.renderChart(); }
      else { $scope.noDataMessage = 'No executions found'; }
      return null;
    }).catch(function (error) {
      console.error('[JobInstances] Error fetching aggregated instances:', error);
      $scope.noDataMessage = (error && error.status === 204) ? 'No executions found in the selected time range' : 'Error loading execution data. Please try again.';
      $scope.aggregatedData = [];
      $scope.totalExecutions = 0;
      $scope.totalPages = 0;
      $scope.page = 0;
    }).finally(function () {
      $scope.loading = false;
      $scope.isLoadingChart = false;
      $scope.isChangingInterval = false;
      $timeout(function () { $scope.$broadcast('rzSliderForceRender'); }, 100);
    });
  }

  // ========================================
  // PAGINATION ("Resolution" — points-per-page)
  // ========================================

  $scope.firstPage = function () { if ($scope.page !== 0) { $scope.page = 0; $scope.loadInstances(); } };
  $scope.lastPage = function () { if ($scope.page < $scope.totalPages - 1) { $scope.page = $scope.totalPages - 1; $scope.loadInstances(); } };
  $scope.nextPage = function () { if ($scope.page < $scope.totalPages - 1) { $scope.page++; $scope.loadInstances(); } };
  $scope.prevPage = function () { if ($scope.page > 0) { $scope.page--; $scope.loadInstances(); } };

  $scope.changePageSize = function (newSize) {
    if ($scope.pageSize === newSize) { return; }
    $scope.pageSize = newSize;
    $scope.page = 0;
    $scope.loadInstances();
  };

  // ========================================
  // CHART RENDERING
  // ========================================

  var chartInstance = null;
  var datasetsByKey = {};

  function buildOrUpdateDataset(key) {
    var def = METRIC_DEFS[key];
    var data = $scope.aggregatedData.map(function (bucket) {
      var v = bucket[key];
      return { x: bucket.intervalStart, y: (v === null || v === undefined) ? null : v };
    }).filter(function (p) { return p.y !== null; });

    var ds = datasetsByKey[key];
    if (ds) {
      ds.data = data;
    } else {
      ds = datasetsByKey[key] = {
        label: def.label,
        data: data,
        borderColor: def.color,
        backgroundColor: def.color + '1a',
        pointBackgroundColor: def.color,
        pointBorderColor: def.color,
        pointRadius: 0, pointHoverRadius: 5, pointHitRadius: 10,
        borderJoinStyle: 'bevel', borderCapStyle: 'butt',
        borderWidth: 2, fill: false, tension: 0.35,
        yAxisID: def.axis
      };
    }
    return ds;
  }

  function getTimeUnit(intervalMinutes) {
    if (intervalMinutes <= 5) { return 'minute'; }
    if (intervalMinutes <= 60) { return 'hour'; }
    return 'day';
  }

  function getMaxTicksLimit(count, intervalMinutes) {
    if (count <= 10) { return count; }
    if (intervalMinutes <= 5) { return Math.min(count, 25); }
    if (intervalMinutes <= 30) { return Math.min(count, 40); }
    if (count <= 50) { return count; }
    return 40;
  }

  $scope.renderChart = function () {
        if ($scope.isLiveMode && chartInstance) LiveChartViewport.capture(chartInstance);
    var canvas = document.getElementById('jobInstancesChart');
    if (!canvas) { $timeout($scope.renderChart, 100); return; }
    var container = canvas.parentElement;
    if (!container || container.offsetParent === null) { $timeout($scope.renderChart, 100); return; }
    var ctx = canvas.getContext('2d');

    var datasets = $scope.metricKeys.filter(function (k) { return $scope.metrics[k]; }).map(buildOrUpdateDataset);

    var intervalMinutes = $scope.intervalSlider.value;
    var timeUnit = getTimeUnit(intervalMinutes);
    var maxTicks = getMaxTicksLimit($scope.aggregatedData.length, intervalMinutes);

    var anyCount = $scope.metricKeys.some(function (k) { return $scope.metrics[k] && METRIC_DEFS[k].axis === 'y-count'; });
    var anyTime = $scope.metricKeys.some(function (k) { return $scope.metrics[k] && METRIC_DEFS[k].axis === 'y-time'; });

    if (chartInstance) {
      chartInstance.data.datasets = datasets;
      chartInstance.options.scales.x.time.unit = timeUnit;
      chartInstance.options.scales.x.ticks.maxTicksLimit = maxTicks;
      chartInstance.options.scales['y-count'].display = anyCount;
      chartInstance.options.scales['y-time'].display = anyTime;

      if ($scope.isLiveMode) {
        if (typeof chartInstance.setActiveElements === 'function') {
          chartInstance.setActiveElements([]);
        }
        if (chartInstance.tooltip && typeof chartInstance.tooltip.setActiveElements === 'function') {
          chartInstance.tooltip.setActiveElements([], { x: 0, y: 0 });
        }

        LiveChartViewport.slide(chartInstance, $scope.timeRangeSlider.minValue, $scope.timeRangeSlider.maxValue);
      } else {
        chartInstance.options.scales.x.min = undefined;
        chartInstance.options.scales.x.max = undefined;
        chartInstance.options.animation = { duration: 0 };
        chartInstance.update('none');
      }
      return;
    }

    chartInstance = new Chart(ctx, {
      type: 'line',
      data: { datasets: datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 0 },
        layout: { padding: { top: 10, bottom: 10 } },
        elements: {
          point: { radius: 0, hoverRadius: 5, hitRadius: 10 },
          line: { borderJoinStyle: 'bevel', borderCapStyle: 'butt' }
        },
        interaction: { mode: 'nearest', intersect: false },
        hover: { mode: 'nearest', intersect: false },
        scales: {
          x: {
            type: 'time',
            time: {
              unit: timeUnit,
              displayFormats: { minute: 'MM/dd HH:mm', hour: 'MM/dd HH:mm', day: 'MM/dd' },
              tooltipFormat: 'MMM dd, yyyy HH:mm:ss'
            },
            title: { display: true, text: 'Time Interval', font: { size: 12, weight: '600' } },
            ticks: { maxRotation: 45, minRotation: 45, font: { size: 11 }, autoSkip: true, maxTicksLimit: maxTicks },
            grid: { display: false },
            min: ($scope.isLiveMode ? $scope.timeRangeSlider.minValue : undefined),
            max: ($scope.isLiveMode ? $scope.timeRangeSlider.maxValue : undefined)
          },
          'y-count': {
            type: 'linear', display: anyCount, position: 'left', beginAtZero: true,
            title: { display: true, text: 'Execution Count', font: { size: 12, weight: '600' } },
            grid: { color: 'rgba(0,0,0,0.05)' },
            ticks: { font: { size: 11 }, callback: function (value) { return Math.round(value); } }
          },
          'y-time': {
            type: 'linear', display: anyTime, position: 'right', beginAtZero: true,
            title: { display: true, text: 'Avg Time (ms)', font: { size: 12, weight: '600' } },
            grid: { drawOnChartArea: false },
            ticks: { font: { size: 11 }, callback: function (value) { return Math.round(value) + ' ms'; } }
          }
        },
        plugins: {
          legend: {
            display: true, position: 'top',
            labels: { usePointStyle: true, padding: 15, font: { size: 12, weight: '600' } }
          },
          tooltip: {
            backgroundColor: 'rgba(0,0,0,0.82)', padding: 12,
            titleFont: { size: 13, weight: 'bold' }, bodyFont: { size: 12 },
            callbacks: {
              label: function (context) {
                var label = context.dataset.label || '';
                var value = context.parsed.y;
                var isTime = context.dataset.yAxisID === 'y-time';
                return isTime ? (label + ': ' + Math.round(value) + ' ms') : (label + ': ' + Math.round(value));
              },
              afterLabel: function (context) {
                if (context.dataset.yAxisID !== 'y-time') { return ''; }
                var bucket = $scope.aggregatedData[context.dataIndex];
                if (!bucket) { return ''; }
                return [
                  'Min: ' + Math.round(bucket.minExecutionTime) + ' ms',
                  'Max: ' + Math.round(bucket.maxExecutionTime) + ' ms',
                  'Total Executions: ' + bucket.totalExecutions
                ];
              }
            }
          }
        }
      }
    });
  };

  // ========================================
  // HELPERS
  // ========================================

  $scope.getSuccessRate = function () {
    if (!$scope.aggregatedData || $scope.aggregatedData.length === 0) { return 100; }
    var totalSuccess = 0, totalExecutions = 0;
    $scope.aggregatedData.forEach(function (dataPoint) {
      totalSuccess += dataPoint.successCount;
      totalExecutions += dataPoint.totalExecutions;
    });
    return totalExecutions > 0 ? ((totalSuccess / totalExecutions) * 100).toFixed(1) : 100;
  };

  // ========================================
  // ACTIONS / CLEANUP
  // ========================================

  $scope.close = function () {
    stopLiveMonitoring();
    if (chartInstance) { LiveChartViewport.cancel(chartInstance); chartInstance.destroy(); chartInstance = null; }
    if (intervalChangeTimeout) { $timeout.cancel(intervalChangeTimeout); }
    if (timeRangeChangeTimeout) { $timeout.cancel(timeRangeChangeTimeout); }
    $mdDialog.hide();
  };

  $scope.$on('$destroy', function () {
    stopLiveMonitoring();
    if (chartInstance) { LiveChartViewport.cancel(chartInstance); chartInstance.destroy(); chartInstance = null; }
    if (intervalChangeTimeout) { $timeout.cancel(intervalChangeTimeout); }
    if (timeRangeChangeTimeout) { $timeout.cancel(timeRangeChangeTimeout); }
  });

  // ========================================
  // INITIALIZATION
  // ========================================

  initializeTimeRange();
});
